// Single source of truth for the Resource detail-page header.
//
// Both render paths — the route's loading branch (skeleton body) and the
// fully-loaded ResourceArticleView — call this builder with the same raw
// `ResourceDetailView` so the header chrome cannot drift between them.
//
// View-only concerns (mutation-aware action buttons, share-to-circle
// trigger) are passed in via `options.actions`. The loading branch passes
// inert placeholder actions; the view passes its real mutation hooks.

import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import { PageHeader, Pill } from '@leanin/ui';
import type { ResourceDetailView } from '@/lib/queries/resources';

type ResourceTranslate = ReturnType<typeof useTranslations<'Resources'>>;

export type BuildResourceHeaderOptions = {
  /** Header actions region (Download PDF / Share to Circle / Edit / Manage).
   *  The loading branch passes inert placeholders; the loaded view passes
   *  mutation-aware buttons (Share-to-Circle popover trigger). */
  readonly actions?: ReactNode;
};

export type BuiltResourceHeader = {
  readonly header: ReactNode;
  readonly cover: { readonly src: string; readonly overlap: number } | undefined;
};

// The header is deliberately spare — title + actions: no lede, kind, or
// date; the body speaks for itself. The draft pill stays as author-facing
// state.
export function buildResourceHeader(
  resource: ResourceDetailView,
  options: BuildResourceHeaderOptions = {},
  t: ResourceTranslate,
): BuiltResourceHeader {
  const draftPill =
    resource.publishedAt === null ? <Pill tone="yellow">{t('detail.draft')}</Pill> : null;

  const header = (
    <PageHeader
      title={resource.title}
      {...(draftPill !== null ? { pills: draftPill } : {})}
      {...(options.actions !== undefined ? { actions: options.actions } : {})}
    />
  );

  // The spare header renders a single title+actions row (~80px card) — the
  // DetailPage default overlap of 80 would land the card's bottom edge flush
  // with the cover's, and fractional-zoom rounding then shows a sliver of
  // photo beneath the card. 40 keeps the card straddling the edge evenly.
  const cover = resource.coverImageUrl
    ? { src: resource.coverImageUrl, overlap: 40 }
    : undefined;

  return { header, cover };
}
